import { useQuery } from "@tanstack/react-query";

import { $api, fetchClient, orNull } from "./client";
import type { components } from "./schema";

export type StockHistoryResponse =
  components["schemas"]["StockHistoryResponse"];
export type RunProjection = components["schemas"]["RunProjection"];
export type StockProjectionResponse =
  components["schemas"]["StockProjectionResponse"];
export type RunProjectionPlan = components["schemas"]["RunProjectionPlan"];
export type StockProjectionPoint =
  components["schemas"]["StockProjectionPoint"];
export type StockHistoryPoint = components["schemas"]["StockHistoryPoint"];
export type ShortageStretch = components["schemas"]["ShortageStretch"];
export type NodeDraw = components["schemas"]["NodeDraw"];

/** On-hand history for one node. */
export const useStockHistory = (
  item: number,
  location: number,
  from?: string,
) =>
  $api.useQuery(
    "get",
    "/api/v1/stock/history/",
    { params: { query: { item, location, from } } },
    // History barely changes; cache it longer than the projection.
    { staleTime: 30 * 60 * 1000 },
  );

/** The run's layers at a node, leaving out the planned order `without`.
 *  Null when the run didn't plan the node. */
export function useRunProjection(
  run: number,
  item: number,
  location: number,
  without?: number,
  { enabled = true } = {},
) {
  const init = {
    params: { path: { id: run }, query: { item, location, without } },
  };
  return useQuery({
    queryKey: $api.queryOptions(
      "get",
      "/api/v1/planning-runs/{id}/projection/",
      init,
    ).queryKey,
    queryFn: ({ signal }) =>
      orNull(
        fetchClient.GET("/api/v1/planning-runs/{id}/projection/", {
          ...init,
          signal,
        }),
      ),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** The node's stock without a plan, from `date` (default today). */
export const useStockProjection = (
  item: number,
  location: number,
  date?: string,
  { enabled = true } = {},
) =>
  $api.useQuery(
    "get",
    "/api/v1/stock/projection/",
    { params: { query: { item, location, date } } },
    { enabled, staleTime: 5 * 60 * 1000 },
  );
